import { AfterViewInit, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IntroductionDocumentConfirmResponse,
  IntroductionUnconfirmedItem,
  isIntroductionDocumentAgreed,
} from 'src/app/shared/models/document-to-learn.model';
import { IHandleError } from 'src/app/core/models/api-responses';
import { FileFromServer } from 'src/app/core/models/files';
import { DocumentsToLearnService } from 'src/app/shared/services/documents-to-learn/documents-to-learn.service';
import { ModalService } from 'src/app/core/services/modal.service';
import { WrapperModalConfig } from 'src/app/shared/modals/content-modal/content-modal.interface';
import { PaInfoBlockComponent } from 'src/app/shared/ui-kit/pa-info-block/pa-info-block.component';
import { PaDataListComponent } from 'src/app/shared/ui-kit/pa-data-list/pa-data-list.component';
import { DataListItem, DataListType } from 'src/app/shared/ui-kit/pa-data-list/models/pa-data-list.model';
import { ApiService } from 'src/app/core/services/api.service';
import { AnalyticsService } from 'src/app/core/services/analytics.service';

@Component({
  selector: 'app-introduction-document-modal',
  standalone: true,
  imports: [PaInfoBlockComponent, PaDataListComponent],
  templateUrl: './document-modal.component.html',
  styleUrls: ['./document-modal.component.scss'],
})
export class DocumentModalComponent implements OnInit, AfterViewInit {
  @Input() data!: IntroductionUnconfirmedItem;
  @Input() onConfirmed?: (item: IntroductionUnconfirmedItem) => void;
  /** Не показывать кнопку подтверждения согласия (режим просмотра для админов / управляющих). */
  @Input() suppressConsentFooter = false;

  fileListData: DataListItem[] = [];
  private isSaving = false;

  private readonly destroy = inject(DestroyRef);
  private readonly documentsToLearnService = inject(DocumentsToLearnService);
  private readonly modalService = inject(ModalService);
  private readonly api = inject(ApiService);
  private readonly analyticsService = inject(AnalyticsService);

  private setConfig(): void {
    const wrapperConfig: WrapperModalConfig = {
      title: this.data?.name,
      footerButtons: this.suppressConsentFooter
        ? []
        : [
            {
              visible: !isIntroductionDocumentAgreed(this.data),
              label: 'Подтверждаю согласие',
              severity: 'primary',
              componentMethod: () => this.save(),
            },
          ],
    };

    this.modalService.setWrapperConfig(wrapperConfig);
  }

  ngOnInit(): void {
    this.fileListData = [
      {
        label: '',
        type: DataListType.FILES,
        value: (this.data?.files ?? []) as FileFromServer[],
      },
    ];
  }

  ngAfterViewInit(): void {
    this.setConfig();
  }

  save(): void {
    if (!this.data?.id || this.isSaving) {
      return;
    }

    this.isSaving = true;

    this.documentsToLearnService
      .confirmDocument(this.data.id)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (res: IntroductionDocumentConfirmResponse | IHandleError) => {
          this.isSaving = false;

          if (!res?.success) {
            return;
          }

          this.documentsToLearnService.refreshUnconfirmedCount();

          const confirmedItem =
            'item' in res && res.item
              ? res.item
              : { ...this.data, confirmed: true };

          this.onConfirmed?.(confirmedItem);

          this.analyticsService.phCapture('Документы: Документ подтверждён');

          this.api.toastPresent('Документ подтвержден', true, '', 'success');

          // Закрываем после завершения клика, чтобы не открыть строку таблицы под курсором.
          setTimeout(() => {
            this.modalService
              .dismissCurrent(null, 'saved')
              .pipe(takeUntilDestroyed(this.destroy))
              .subscribe();
          }, 0);
        },
        error: () => {
          this.isSaving = false;
        },
      });
  }

  handleClose(): void {
    this.modalService.dismissCurrent().pipe(takeUntilDestroyed(this.destroy)).subscribe();
  }
}